import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashAnswersFeedback } from "@mirage/analytics/events/types/click_dash_answers_feedback";
import { PAP_Click_DashAnswersHide } from "@mirage/analytics/events/types/click_dash_answers_hide";
import { PAP_Click_DashAnswersShow } from "@mirage/analytics/events/types/click_dash_answers_show";
import { PAP_Click_DashAnswersSource } from "@mirage/analytics/events/types/click_dash_answers_source";
import { PAP_Click_DashAnswersTaggedFeedback } from "@mirage/analytics/events/types/click_dash_answers_tagged_feedback";
import { PAP_Copy_DashAnswers } from "@mirage/analytics/events/types/copy_dash_answers";
import { PAP_Expand_DashAnswers } from "@mirage/analytics/events/types/expand_dash_answers";
import { PAP_Shown_DashAnswersSource } from "@mirage/analytics/events/types/shown_dash_answers_source";
import { PAP_Shown_DashSearchResult } from "@mirage/analytics/events/types/shown_dash_search_result";
import { ANSWER_DWELL_END_REASONS } from "@mirage/analytics/session/session-utils";
import { ChatEntryPoint } from "@mirage/conversations/types";
import { ConversationMessages } from "@mirage/mosaics/Chat/components/chat/ConversationMessages";
import { SuggestedQueryChips } from "@mirage/mosaics/Chat/components/chat/suggestions/SuggestedQueryChips";
import { AnswerCitationCards } from "@mirage/mosaics/MultiAnswersCard/AnswerCitationCard";
import { AnswerFollowUpInput } from "@mirage/mosaics/MultiAnswersCard/layout/AnswerFollowUpInput";
import {
  MultiAnswerCardContainer,
  type MultiAnswerCardContainerBaseProps,
  MultiAnswerCardContainerDivider,
} from "@mirage/mosaics/MultiAnswersCard/layout/MultiAnswerCardContainer";
import { measurePerformance } from "@mirage/service-operational-metrics/measure-performance";
import { PERFORMANCE_MARKS } from "@mirage/service-operational-metrics/performance-constants";
import { copyToClipboard } from "@mirage/service-platform-actions";
import { openResult } from "@mirage/service-result-actions";
import { usePageVisibility } from "@mirage/shared/hooks/usePageVisibility";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import { FeedbackOptions } from "@mirage/shared/types";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { Answer } from "./Answer";
import { NoAnswer } from "./NoAnswer";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { DashAnswersFeedbackTag } from "@mirage/analytics/events/enums/dash_answers_feedback_tag";
import type { FeatureLine } from "@mirage/analytics/events/enums/feature_line";
import {
  urlForConversationWithID,
  urlForConversationWithServerID,
} from "@mirage/mosaics/Chat/utils/navigation";
import { useComposeAnalyticsContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeAnalyticsContext";
import { type AnswerConversationProps } from "@mirage/mosaics/MultiAnswersCard/useInitialConversation";
import type { Connectors } from "@mirage/service-connectors/service";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import type {
  MultiAnswerResponse,
  QuestionAndAnswerResponse,
  QuestionAndAnswerSource,
} from "@mirage/shared/answers/multi-answer";

const featureLine: FeatureLine = "multi_answers";

export type MultiAnswerCardProps = {
  multiAnswerResponse: MultiAnswerResponse | undefined;
  connectors: Connectors;
  loading?: boolean;
  query: string;
  onShowFeedbackSnackbar: () => void;
  onShowCopyMessageSnackbar: () => void;
  annotationMode?: boolean;
  onOpenAnnotationModal: (
    feedback: FeedbackOptions,
    answer: QuestionAndAnswerResponse,
  ) => void;
  isStreamingEnabled?: boolean;
  variant: "narrow" | "wide";
  conversationProps: AnswerConversationProps;
  answersStreaming?: boolean;
};
export const MultiAnswerCard: React.FC<MultiAnswerCardProps> = ({
  multiAnswerResponse,
  conversationProps,
  connectors,
  loading = false,
  query,
  onShowFeedbackSnackbar,
  onShowCopyMessageSnackbar,
  annotationMode = false,
  onOpenAnnotationModal,
  isStreamingEnabled,
  variant,
  answersStreaming,
}) => {
  const isPageVisible = usePageVisibility();
  const isCardVisible = useRef(false);
  const { searchQueryUuid } = useSearchQueryId();
  const navigate = useNavigate();
  const { reportPapEvent, implicitAnswerDwellManager, searchSessionManager } =
    useMirageAnalyticsContext();
  const { logComposeEvent } = useComposeAnalyticsContext({
    actionSurfaceComponent: "compose_chat_pane",
  });
  const conversation = useConversation(
    (conversationProps.isEnabled && conversationProps.conversationId) ||
      undefined,
  );

  // Redirect to /chat if the query used the 'write' agent
  const usesWriteAgent = useMemo(() => {
    if (conversation?.messages.length && conversation?.serverConversationId) {
      const foundAssistantMessage = conversation.messages.find(
        (m) => m.messageType.case === "assistantMessage",
      );
      if (
        foundAssistantMessage &&
        foundAssistantMessage.messageType.case === "assistantMessage"
      ) {
        return foundAssistantMessage.messageType.value.agentId === "write";
      }
    }
    return false;
  }, [conversation?.messages, conversation?.serverConversationId]);
  useEffect(() => {
    if (usesWriteAgent && conversation?.serverConversationId) {
      navigate(
        urlForConversationWithServerID(conversation.serverConversationId),
        {
          replace: true,
        },
      );
    }
  }, [usesWriteAgent, conversation?.serverConversationId, navigate]);

  const isEmpty = conversationProps.isEnabled
    ? !conversationProps.messages.length
    : !multiAnswerResponse?.answers || multiAnswerResponse.answers.length === 0;

  // Ensure we don't log end session events when component re-renders due to loading or change in answers
  const canToggleSession = !isEmpty && !loading && !answersStreaming;

  // Create answer ID for logging purposes
  const answerLoggingId = useRef(uuid());
  const implicitAnswerDwellSessionRequestId = useRef<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (
      implicitAnswerDwellSessionRequestId.current !==
        multiAnswerResponse?.requestId &&
      canToggleSession
    ) {
      // Create dwell session
      implicitAnswerDwellManager.createSession();
      implicitAnswerDwellManager.updateProperties({
        queryString: query,
        entryPoint: ChatEntryPoint.search_bar_inline,
        searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
        searchRequestId: multiAnswerResponse?.requestId,
        featureLine,
      });
      implicitAnswerDwellSessionRequestId.current =
        multiAnswerResponse?.requestId;

      // Log shown event for each answer
      multiAnswerResponse?.answers?.forEach((answer) => {
        reportPapEvent(
          PAP_Shown_DashSearchResult({
            queryString: query,
            answerString: answer.answer,
            generatedQueryString: answer.question,
            answerId: answerLoggingId.current,
            numAnswerSources: answer.sources.length,
            entryPoint: ChatEntryPoint.search_bar_inline,
            searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
            searchRequestId: multiAnswerResponse?.requestId,
            searchQueryUuid,
            featureLine,
          }),
        );
      });
    }
  }, [
    canToggleSession,
    implicitAnswerDwellManager,
    multiAnswerResponse?.answers,
    multiAnswerResponse?.requestId,
    query,
    reportPapEvent,
    searchQueryUuid,
    searchSessionManager,
  ]);

  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );
  useEffect(() => {
    if (!contentElement) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Answer card is visible in viewport
          isCardVisible.current = true;
          implicitAnswerDwellManager.extendOrCreateSession();
          implicitAnswerDwellManager.updateProperties({
            queryString: query,
            entryPoint: ChatEntryPoint.search_bar_inline,
            searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
            searchQueryUuid: searchQueryUuid,
            searchRequestId: multiAnswerResponse?.requestId,
            featureLine,
          });
        } else {
          // User has scrolled the answer card out of view. We should end the session
          isCardVisible.current = false;
          implicitAnswerDwellManager.endSession(
            ANSWER_DWELL_END_REASONS.SCROLLED,
          );
        }
      });
    });

    observer.observe(contentElement);
    return () => {
      if (contentElement) {
        observer.unobserve(contentElement);
      }
    };
  }, [
    contentElement,
    multiAnswerResponse?.requestId,
    searchQueryUuid,
    query,
    implicitAnswerDwellManager,
    searchSessionManager,
  ]);

  useEffect(() => {
    if (isPageVisible && isCardVisible.current) {
      // User switched back to the page and answer card is in view
      implicitAnswerDwellManager.extendOrCreateSession();
      implicitAnswerDwellManager.updateProperties({
        queryString: query,
        entryPoint: ChatEntryPoint.search_bar_inline,
        searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
        searchQueryUuid: searchQueryUuid,
        searchRequestId: multiAnswerResponse?.requestId,
        featureLine,
      });
    } else if (!isPageVisible) {
      // User has switched tabs / minimized window / minimized app, we should end the sessions
      implicitAnswerDwellManager.endSession(
        ANSWER_DWELL_END_REASONS.LOST_FOCUS,
      );
    }
  }, [
    implicitAnswerDwellManager,
    isPageVisible,
    multiAnswerResponse?.requestId,
    query,
    searchQueryUuid,
    searchSessionManager,
  ]);

  useEffect(() => {
    if (canToggleSession) {
      performance.mark(PERFORMANCE_MARKS.MULTI_ANSWERS.ANSWERS_RENDERED); // Mark when answers are rendered

      // Measure the perf / latency of fetching and rendering answers
      measurePerformance({
        metricNamespace: "multi-answers",
        measureName: "tta", // "time to answer"
        startMark: PERFORMANCE_MARKS.MULTI_ANSWERS.FETCH_START,
        endMark: PERFORMANCE_MARKS.MULTI_ANSWERS.ANSWERS_RENDERED,
      });
    }
  }, [canToggleSession]);

  const getCommonPapFields = useCallback(() => {
    return {
      queryString: query,
      answerId: answerLoggingId.current,
      entryPoint: ChatEntryPoint.search_bar_inline,
      featureLine: "multi_answers" as FeatureLine,
      searchQueryUuid,
      searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
      searchRequestId: multiAnswerResponse?.requestId,
      conversationId: conversation?.serverConversationId || "",
      assistRequestId: conversation?.assistRequestId,
    };
  }, [
    multiAnswerResponse?.requestId,
    query,
    searchSessionManager,
    searchQueryUuid,
    conversation?.serverConversationId,
    conversation?.assistRequestId,
  ]);

  const handleCopyMessage = (item: QuestionAndAnswerResponse) => {
    onShowCopyMessageSnackbar();
    copyToClipboard(item.answer || "");

    searchSessionManager.extendOrCreateSession("multi_answer_action");
    reportPapEvent(
      PAP_Copy_DashAnswers({
        answerPositionIndex: item.position,
        answerString: item.answer,
        generatedQueryString: item.question,
        ...getCommonPapFields(),
      }),
    );
  };

  const handleSourceClick = async (
    item: QuestionAndAnswerResponse,
    source: QuestionAndAnswerSource,
  ) => {
    searchSessionManager.extendOrCreateSession("multi_answer_action");
    reportPapEvent(
      PAP_Click_DashAnswersSource({
        answerPositionIndex: item.position,
        answerString: item.answer,
        dashObjectId: source.uuid,
        ...getCommonPapFields(),
      }),
    );

    await openResult(source);
  };

  const handleClickFeedback = (
    item: QuestionAndAnswerResponse,
    feedback: FeedbackOptions,
  ) => {
    searchSessionManager.extendOrCreateSession("multi_answer_action");
    reportPapEvent(
      PAP_Click_DashAnswersFeedback({
        answerPositionIndex: item.position,
        answerString: item.answer,
        dashAnswerFeedback: feedback,
        generatedQueryString: item.question,
        ...getCommonPapFields(),
      }),
    );
    if (annotationMode && feedback === FeedbackOptions.Negative) {
      onOpenAnnotationModal(feedback, item);
    }
  };

  const handleClickSecondaryFeedback = (
    item: QuestionAndAnswerResponse,
    feedback: DashAnswersFeedbackTag,
  ) => {
    searchSessionManager.extendOrCreateSession("multi_answer_action");
    reportPapEvent(
      PAP_Click_DashAnswersTaggedFeedback({
        answerPositionIndex: item.position,
        answerString: item.answer,
        dashAnswersFeedbackTag: feedback,
        ...getCommonPapFields(),
      }),
    );
  };

  const handleExpand = (item: QuestionAndAnswerResponse) => {
    searchSessionManager.extendOrCreateSession("multi_answer_action");
    reportPapEvent(
      PAP_Click_DashAnswersShow({
        answerPositionIndex: item.position,
        generatedQueryString: item.question,
        ...getCommonPapFields(),
      }),
    );
  };

  const handleCollapse = (item: QuestionAndAnswerResponse) => {
    searchSessionManager.extendOrCreateSession("multi_answer_action");
    reportPapEvent(
      PAP_Click_DashAnswersHide({
        answerPositionIndex: item.position,
        generatedQueryString: item.question,
        ...getCommonPapFields(),
      }),
    );
  };

  const handleShownSource = useCallback(
    (item: QuestionAndAnswerResponse, source: QuestionAndAnswerSource) => {
      reportPapEvent(
        PAP_Shown_DashAnswersSource({
          answerPositionIndex: item.position,
          generatedQueryString: item.question,
          resultUuid: source.uuid,
          resultUpdatedAtMs: source.lastUpdatedMs,
          connector: source.connectorName,
          ...getCommonPapFields(),
        }),
      );
    },
    [reportPapEvent, getCommonPapFields],
  );

  const followups =
    conversationProps.isEnabled &&
    conversationProps.loadingState !== "error" ? (
      <MultiAnswerCardFollowUps
        conversationProps={conversationProps}
        loading={loading}
        logComposeEvent={logComposeEvent}
        conversationId={conversation?.serverConversationId}
        assistRequestId={conversation?.assistRequestId}
        searchSessionId={searchSessionManager.getSessionIdOrUndefined()}
      />
    ) : null;
  const answerContent = (
    <>
      {/* Loaded, no answer available */}
      {!loading && isEmpty && (
        <Fragment>
          <NoAnswer />
          <MultiAnswerCardContainerDivider />
        </Fragment>
      )}

      {/* Conversation-based answer */}
      {conversationProps.isEnabled && conversationProps.messages.length > 0 && (
        <ConversationMessages
          variant="default"
          noPadding
          messages={conversationProps.messages}
          isWaitingForResponse={loading}
          progressString={undefined}
          onRemoveSource={() => {}}
          isLastMessageFollowUpSuggestions={false}
          artifacts={[]}
          logComposeEvent={logComposeEvent}
          onOpenArtifact={() => {}}
          hideFirstUserMessage={true}
          hideSources
          conversationId={conversation?.serverConversationId || ""}
          searchSessionId={searchSessionManager.getSessionIdOrUndefined()}
          assistRequestId={conversation?.assistRequestId}
        />
      )}

      {/* Traditional answers */}
      {!conversationProps.isEnabled &&
        !loading &&
        !isEmpty &&
        multiAnswerResponse?.answers?.map((answer, index) => {
          return (
            <Fragment key={answer.answerId || answer.conversationId || ""}>
              <Answer
                answer={answer}
                answerId={answer.answerId || answer.conversationId || ""}
                expandedByDefault={index == 0 || Boolean(isStreamingEnabled)}
                onShowFeedbackSnackbar={onShowFeedbackSnackbar}
                onCopyMessage={handleCopyMessage}
                onSourceClick={handleSourceClick}
                onClickFeedback={handleClickFeedback}
                onClickSecondaryFeedback={handleClickSecondaryFeedback}
                onShownSource={handleShownSource}
                onCollapse={() => {
                  handleCollapse(answer);
                }}
                onExpand={() => {
                  handleExpand(answer);
                }}
                annotationMode={annotationMode}
                onClickMore={() => {
                  reportPapEvent(
                    PAP_Expand_DashAnswers({
                      answerString: answer.answer,
                      answerPositionIndex: answer.position,
                      ...getCommonPapFields(),
                    }),
                  );
                }}
                isStreamingEnabled={isStreamingEnabled}
              />
              <MultiAnswerCardContainerDivider />
            </Fragment>
          );
        })}
    </>
  );

  return (
    <MultiAnswerCardContainer
      variant={variant}
      loadingState={getLoadingState(loading, conversationProps)}
      followups={followups}
      contentRef={setContentElement}
      connectors={connectors}
      answer={answerContent}
      citations={
        conversationProps.isEnabled &&
        conversationProps.loadingState !== "error" ? (
          <AnswerCitationCards
            messages={conversationProps.messages}
            conversationId={conversation?.serverConversationId || ""}
            assistRequestId={conversation?.assistRequestId}
            searchSessionId={searchSessionManager.getSessionIdOrUndefined()}
          />
        ) : null
      }
      commonPapFields={getCommonPapFields()}
    />
  );
};

interface MultiAnswerCardFollowUpsProps {
  conversationProps: AnswerConversationProps;
  loading: boolean;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  assistRequestId?: string;
  searchSessionId?: string;
}
/**
 * This component helps route controlling logic to the presentational follow up components like
 * SuggestedQueryChips and AnswerFollowUpInput
 */
const MultiAnswerCardFollowUps = ({
  conversationProps,
  loading,
  logComposeEvent,
  conversationId,
  assistRequestId,
  searchSessionId,
}: MultiAnswerCardFollowUpsProps) => {
  const navigate = useNavigate();
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const handleSubmitFollowUp = useCallback(
    async (query: string) => {
      if (!conversationProps.isEnabled) {
        throw new Error(
          "Follow up should only be shown on conversation-enabled Answer",
        );
      }
      if (!conversationProps.conversationId) {
        throw new Error(
          "Follow up should only be shown after conversation is loaded",
        );
      }
      if (waitingForResponse) {
        return; // no-op if submitting multiple times
      }
      setWaitingForResponse(true); // disable input while waiting for message to be posted
      await conversationProps.postFollowUpMessage(query);
      navigate(urlForConversationWithID(conversationProps.conversationId));
    },
    [conversationProps, navigate, waitingForResponse],
  );
  return !loading &&
    conversationProps.isEnabled &&
    conversationProps.messages.length > 0 ? (
    <>
      <SuggestedQueryChips
        suggestedQueries={conversationProps.suggestedQueries.map(
          (query) => query.text,
        )}
        onSubmit={handleSubmitFollowUp}
        logComposeEvent={logComposeEvent}
        conversationId={conversationId}
        assistRequestId={assistRequestId}
        searchSessionId={searchSessionId}
      />
      <AnswerFollowUpInput
        onSubmit={handleSubmitFollowUp}
        waitingForResponse={
          waitingForResponse || conversationProps.loadingState !== "done"
        }
        logComposeEvent={logComposeEvent}
        conversationId={conversationId}
        assistRequestId={assistRequestId}
        searchSessionId={searchSessionId}
      />
    </>
  ) : null;
};

function getLoadingState(
  loading: boolean,
  conversationProps: AnswerConversationProps,
): MultiAnswerCardContainerBaseProps["loadingState"] {
  if (conversationProps.isEnabled) {
    return conversationProps.loadingState;
  }
  return loading ? "initiating" : "done";
}
